/**
 * Event-day check-in (/checkin/:eventId?mode=scan|kiosk&session=<id>).
 * Standalone (no admin sidebar) so ushers only see this screen.
 *   - scan:  badge QR via camera → instant check-in, big result card
 *   - kiosk: operator searches CI / ADB code / name for people without badge,
 *            phone or internet access
 * Check-ins are per session (room entrance) and feed the live quorum counter.
 * Scans made while offline are queued on the device and retried.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ScanLine, Search, ShieldAlert, Users, WifiOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/authContext';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/supabase.config';
import { parseLocalDate } from '@/models/event';
import { QrScanner } from '../components/QrScanner';
import { CheckinResultCard } from '../components/CheckinResultCard';
import { BadgeStickerPrint, type BadgeStickerData } from '../../print/BadgeStickers';
import {
  checkinByToken,
  checkinParticipant,
  extractTokenFromQr,
  getAttendanceSummary,
  searchCheckinParticipants,
  type CheckinResult,
  type CheckinSearchRow,
} from '../checkinRepository';

type Mode = 'scan' | 'kiosk';
const GENERAL = 'general';

interface QueuedScan {
  token: string;
  sessionId: string | null;
  at: number;
}

function isNetworkError(err: unknown) {
  return !navigator.onLine || (err instanceof Error && /fetch|network|load failed/i.test(err.message));
}

export function EventCheckinPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const location = useLocation();
  const { canAny, isLoading: permsLoading } = usePermissions();

  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (permsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!canAny('event_participant.scan', 'event_participant.checkin')) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="size-10 text-muted-foreground" />
        <p className="text-muted-foreground">Su usuario no tiene permiso para registrar ingresos.</p>
      </div>
    );
  }
  return <CheckinScreen eventId={eventId as string} />;
}

function CheckinScreen({ eventId }: { eventId: string }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: Mode = searchParams.get('mode') === 'kiosk' ? 'kiosk' : 'scan';
  const sessionParam = searchParams.get('session');

  const [result, setResult] = useState<CheckinResult | null>(null);
  const [pending, setPending] = useState<{ token?: string; participantId?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState<{ name: string; at: string; ok: boolean }[]>([]);
  const [sticker, setSticker] = useState<BadgeStickerData | null>(null);
  const queueKey = `events.checkinQueue.${eventId}`;
  const [queue, setQueue] = useState<QueuedScan[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(queueKey) ?? '[]');
    } catch {
      return [];
    }
  });

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  // ─── Event + sessions ─────────────────────────────────────────────────────
  const { data: event } = useQuery({
    queryKey: ['checkin_event', eventId],
    queryFn: async () => {
      const { data, error } = await supabase.from('events').select('id, code, name, status').eq('id', eventId).single();
      if (error) throw new Error(error.message);
      return data;
    },
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ['checkin_sessions', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_sessions')
        .select('id, name, session_date, start_time')
        .eq('event_id', eventId)
        .order('session_date')
        .order('start_time');
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  // Default: first session happening today, else general entrance
  useEffect(() => {
    if (sessionParam || sessions.length === 0) return;
    const today = new Date().toDateString();
    const todays = sessions.find((s) => parseLocalDate(s.session_date)?.toDateString() === today);
    if (todays) setParam('session', todays.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  const sessionId = sessionParam && sessionParam !== GENERAL ? sessionParam : null;

  const { data: summary } = useQuery({
    queryKey: ['attendance_summary', eventId],
    queryFn: () => getAttendanceSummary(eventId),
    refetchInterval: 15_000,
  });
  const sessionStats = summary?.sessions.find((s) => s.session_id === sessionId);

  // ─── Offline queue ────────────────────────────────────────────────────────
  const saveQueue = useCallback(
    (next: QueuedScan[]) => {
      setQueue(next);
      try {
        localStorage.setItem(queueKey, JSON.stringify(next));
      } catch {
        // storage unavailable — queue lives in memory only
      }
    },
    [queueKey],
  );

  useEffect(() => {
    if (queue.length === 0) return;
    const timer = setInterval(async () => {
      if (!navigator.onLine) return;
      const remaining: QueuedScan[] = [];
      for (const item of queue) {
        try {
          // Usher already saw the badge in person; warnings are reviewed later
          await checkinByToken({ token: item.token, eventId, sessionId: item.sessionId, force: true });
        } catch (err) {
          if (isNetworkError(err)) remaining.push(item);
        }
      }
      saveQueue(remaining);
      queryClient.invalidateQueries({ queryKey: ['attendance_summary', eventId] });
    }, 10_000);
    return () => clearInterval(timer);
  }, [queue, eventId, saveQueue, queryClient]);

  // ─── Check-in actions ─────────────────────────────────────────────────────
  const dismissTimer = useRef<ReturnType<typeof setTimeout>>();

  const showResult = useCallback(
    (r: CheckinResult) => {
      setResult(r);
      if (r.participant) {
        setRecent((prev) =>
          [
            { name: r.participant?.full_name ?? '', at: new Date().toISOString(), ok: r.result === 'OK' || r.result === 'ALREADY' },
            ...prev,
          ].slice(0, 10),
        );
      }
      if (navigator.vibrate) navigator.vibrate(r.result === 'OK' ? 80 : [80, 60, 80]);
      clearTimeout(dismissTimer.current);
      if (r.result === 'OK') dismissTimer.current = setTimeout(() => setResult(null), 2500);
      queryClient.invalidateQueries({ queryKey: ['attendance_summary', eventId] });
    },
    [eventId, queryClient],
  );

  const run = useCallback(
    async (target: { token?: string; participantId?: string }, force = false) => {
      setBusy(true);
      setPending(target);
      try {
        const r = target.token
          ? await checkinByToken({ token: target.token, eventId, sessionId, force, method: 'QR' })
          : await checkinParticipant({ participantId: target.participantId as string, sessionId, force, method: 'KIOSK' });
        showResult(r);
      } catch (err) {
        if (target.token && isNetworkError(err)) {
          saveQueue([...queue, { token: target.token, sessionId, at: Date.now() }]);
          setResult(null);
          setRecent((prev) => [{ name: 'Guardado sin conexión', at: new Date().toISOString(), ok: true }, ...prev].slice(0, 10));
        } else {
          setResult({ result: 'EVENT_NOT_ACTIVE', warnings: [] });
          console.error(err);
        }
      } finally {
        setBusy(false);
      }
    },
    [eventId, sessionId, showResult, saveQueue, queue],
  );

  const handleScan = useCallback(
    (text: string) => {
      if (busy || result) return;
      const token = extractTokenFromQr(text);
      if (!token) {
        showResult({ result: 'NOT_FOUND' });
        return;
      }
      void run({ token });
    },
    [busy, result, run, showResult],
  );

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="sticky top-0 z-10 space-y-2 border-b bg-background px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-semibold">{event?.name ?? 'Cargando...'}</p>
            <p className="text-xs text-muted-foreground">Registro de ingreso</p>
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-1 text-sm font-medium">
            <Users className="size-4" />
            {sessionId ? (sessionStats?.attended ?? '—') : (summary?.event_attended ?? '—')}
            <span className="text-muted-foreground">/ {summary?.registered ?? '—'}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={sessionParam ?? GENERAL} onValueChange={(v) => setParam('session', v)}>
            <SelectTrigger className="flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={GENERAL}>Ingreso general</SelectItem>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} · {parseLocalDate(s.session_date)?.toLocaleDateString('es-BO', { day: 'numeric', month: 'short' })}
                  {s.start_time && ` ${s.start_time.slice(0, 5)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SegmentedControl
            value={mode}
            onChange={(m) => {
              setResult(null);
              setParam('mode', m);
            }}
            options={[
              { value: 'scan' as Mode, label: 'Escáner' },
              { value: 'kiosk' as Mode, label: 'Kiosco' },
            ]}
          />
        </div>
        {sessionId && sessionStats && (
          <p className="text-xs text-muted-foreground">
            Quórum de la sesión: {sessionStats.attended_ministers} ministros presentes de {summary?.registered_ministers ?? '—'} inscritos
          </p>
        )}
        {queue.length > 0 && (
          <p className="flex items-center gap-1 text-xs text-amber-700">
            <WifiOff className="size-3" /> {queue.length} ingreso(s) pendientes de sincronizar
          </p>
        )}
      </header>

      <main className="mx-auto max-w-md space-y-4 p-4">
        {mode === 'scan' ? (
          <>
            <QrScanner onScan={handleScan} paused={!!result || busy} />
            {busy && !result && (
              <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Verificando...
              </p>
            )}
            {!result && !busy && (
              <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <ScanLine className="size-4" /> Apunte la cámara al QR de la credencial
              </p>
            )}
          </>
        ) : (
          !result && <KioskSearch eventId={eventId} sessionId={sessionId} onPick={(id) => run({ participantId: id })} busy={busy} />
        )}

        {result && (
          <CheckinResultCard
            result={result}
            busy={busy}
            onConfirm={pending ? () => run(pending, true) : undefined}
            onDismiss={() => setResult(null)}
            onPrintSticker={
              result.participant
                ? () =>
                    setSticker({
                      token: result.participant!.public_token,
                      full_name: result.participant!.full_name ?? '',
                      dni: result.participant!.dni,
                      adb_code: result.participant!.adb_code,
                      district: result.participant!.district_name,
                      region: result.participant!.region_name,
                      kind: result.participant!.kind,
                    })
                : undefined
            }
          />
        )}

        {recent.length > 0 && (
          <section className="rounded-lg border bg-card p-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">Últimos registros en este dispositivo</p>
            <ul className="space-y-1 text-sm">
              {recent.map((r, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className={`truncate ${r.ok ? '' : 'text-red-700'}`}>{r.name}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {new Date(r.at).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      {sticker && <BadgeStickerPrint stickers={[sticker]} onClose={() => setSticker(null)} />}
    </div>
  );
}

function KioskSearch({
  eventId,
  sessionId,
  onPick,
  busy,
}: {
  eventId: string;
  sessionId: string | null;
  onPick: (participantId: string) => void;
  busy: boolean;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['checkin_search', eventId, sessionId, debounced],
    queryFn: () => searchCheckinParticipants(eventId, debounced, sessionId),
    enabled: debounced.length >= 2,
  });

  const checkedAt = (r: CheckinSearchRow) => (sessionId ? r.session_checked_in_at : r.event_checked_in_at);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="CI, código de ministro o nombre"
          className="h-14 pl-11 text-lg"
          autoComplete="off"
        />
        {isFetching && <Loader2 className="absolute right-3 top-1/2 size-5 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>

      {debounced.length >= 2 && !isFetching && rows.length === 0 && (
        <p className="rounded-md border bg-card p-4 text-center text-sm text-muted-foreground">
          No está inscrito en este evento. Envíelo a la mesa de registro.
        </p>
      )}

      <ul className="space-y-2">
        {rows.map((r) => {
          const at = checkedAt(r);
          return (
            <li key={r.participant_id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onPick(r.participant_id)}
                className="flex w-full items-center justify-between gap-3 rounded-lg border bg-card p-4 text-left hover:bg-muted disabled:opacity-60"
              >
                <span className="min-w-0">
                  <span className="block truncate text-lg font-medium">{r.full_name}</span>
                  <span className="block truncate text-sm text-muted-foreground">
                    {r.adb_code ?? (r.dni && `CI ${r.dni}`)}
                    {r.district_name && ` · ${r.district_name}`}
                  </span>
                </span>
                <span className="shrink-0 text-right text-xs">
                  {at ? (
                    <span className="text-blue-700">
                      Ingresó {new Date(at).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  ) : r.registration_status === 'REJECTED' ? (
                    <span className="text-red-700">Rechazada</span>
                  ) : r.registration_status === 'PRE_REGISTERED' ? (
                    <span className="text-amber-700">Por verificar</span>
                  ) : r.payment_status !== 'PAID' ? (
                    <span className="text-amber-700">Pago pendiente</span>
                  ) : (
                    <span className="text-green-700">Listo</span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
